// text-animation.js
import SplitType from 'split-type';

function afterLoader(cb) {
    let done = false;
    const finish = () => {
        if (done) return;
        done = true;
        cb();
    };

    document.addEventListener('loader:hide', finish, { once: true });

    const loader = document.querySelector('.loading, .o-loading, [data-module-loader]');
    if (!loader) {
        requestAnimationFrame(() => setTimeout(finish, 0));
        return;
    }

    if (loader.classList.contains('-hidden') || loader.getAttribute('aria-hidden') === 'true') {
        finish();
        return;
    }

    const mo = new MutationObserver(() => {
        if (loader.classList.contains('-hidden') || loader.getAttribute('aria-hidden') === 'true') {
            mo.disconnect();
            finish();
        }
    });
    mo.observe(loader, { attributes: true, attributeFilter: ['class', 'aria-hidden'] });
}

function observeInview(els) {
    if (!els.length) return;

    const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
            if (e.isIntersecting) {
                e.target.classList.add('is-inview');
                io.unobserve(e.target);
            }
        });
    }, {
        root: null,
        threshold: 0.01,
        rootMargin: '0px 0px -10% 0px',
    });

    els.forEach((el) => {
        if (!el.classList.contains('is-inview')) {
            io.observe(el);
        }
    });

    const checkNow = (el) => {
        if (el.classList.contains('is-inview')) return;
        const r = el.getBoundingClientRect();
        const vh = window.innerHeight || document.documentElement.clientHeight;
        if (r.top < vh * 0.9 && r.bottom > 0) {
            el.classList.add('is-inview');
            io.unobserve(el);
        }
    };

    els.forEach(checkNow);
}

function revealWaiters(waiters) {
    waiters.forEach((el) => {
        if (!el.classList.contains('is-inview')) {
            el.classList.add('is-inview');
        }
    });
}

function appearAnimations(root = document) {
    const waiters = [...root.querySelectorAll('.u-appear[data-appear-wait-loader]')];
    const others = [...root.querySelectorAll('.u-appear:not([data-appear-wait-loader])')];

    const scrollOthers = others.filter((el) => !el.hasAttribute('data-scroll'));
    observeInview(scrollOthers);

    if (waiters.length) {
        afterLoader(() => revealWaiters(waiters));
    }
}

function textAnimations(root = document) {
    const els = [...root.querySelectorAll('[data-split]')].filter(
        (el) => !el.hasAttribute('data-split-processed')
    );

    if (!els.length) {
        appearAnimations(root);
        return;
    }

    els.forEach((splitElt) => {
        const splitTypeAttr = splitElt.getAttribute('data-split');
        const types = splitTypeAttr && splitTypeAttr.trim() ? splitTypeAttr : 'lines, words';
        const split = new SplitType(splitElt, { types });

        // SplitType wraps lines in block <div>s: textContent concatenates them
        // without spaces ("e-commercepour"). Trailing space keeps words separated for SEO tools.
        split.lines?.forEach((line, i, arr) => {
            if (i < arr.length - 1 && !/\s$/.test(line.textContent || '')) {
                line.appendChild(document.createTextNode(' '));
            }
        });

        if (splitElt.hasAttribute('data-split-animation')) {
            split.lines?.forEach((l, i) => l.style.setProperty('--l-index', i));
            split.words?.forEach((w, i) => w.style.setProperty('--w-index', i));
            split.chars?.forEach((c, i) => c.style.setProperty('--c-index', i));
        }

        splitElt.setAttribute('data-split-processed', '');
    });

    const waiters = els.filter((el) => el.hasAttribute('data-split-wait-loader'));
    const others = els.filter((el) => !el.hasAttribute('data-split-wait-loader'));

    observeInview(others);

    if (waiters.length) {
        afterLoader(() => revealWaiters(waiters));
    }

    appearAnimations(root);
}

export { textAnimations };
